import { formatInTimeZone } from 'date-fns-tz';
import { AUTHENTICATION_TYPES } from '@/utils';

const SSO_LAST_SYNC_DATE_FORMAT = 'MMMM d, yyyy h:mm a';

const parseUtcDate = (value) => {
  if (!value) return null;

  const hasTimeZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  const normalizedValue = hasTimeZone ? value : `${value}Z`;
  const parsed = new Date(normalizedValue);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatSsoLastSyncDate = (date, timeZoneId) => {
  return formatInTimeZone(date, timeZoneId, SSO_LAST_SYNC_DATE_FORMAT);
};

export default {
  loading(state) {
    return state.loading;
  },

  officerById(state) {
    return (id) => state.organization.officers.find((x) => x.id === id);
  },

  assigningOfficerById(state) {
    return (id) => state.trainingOfficers.find((x) => x.id === id);
  },

  organization(state) {
    return state.organization;
  },

  displayLastNameBeforeFirstName(state) {
    return state.organization.displayLastNameBeforeFirstName;
  },

  authenticationType(state) {
    return state.organization.authenticationType;
  },

  usageTotal(state) {
    return state.usage.totalItems;
  },

  dailyUsage(state) {
    return state.usage.dailyUsage;
  },

  officerUsage(state) {
    return state.usage.officerUsage;
  },

  usageOfficers(state) {
    return state.usage.officers;
  },

  userDetailInfo(state) {
    return state.userDetail.user;
  },

  userDetailAssignedCourses(state) {
    return state.userDetail.assignedCourses;
  },

  userDetailDailyUsage(state) {
    return state.userDetail.userDailyUsage;
  },

  courseAssignments(state) {
    return state.assign.courseAssignments;
  },

  totalCourseAssignments(state) {
    return state.assign.totalItems;
  },

  allSupportRequests(state) {
    return state.allSupportRequests;
  },

  supportRequests(state) {
    return state.supportRequests;
  },

  users(state) {
    return state.users;
  },

  officersInOrgDashboard(state) {
    return state.officersInOrgDashboard.officerList;
  },

  totalOfficersInOrgDashboard(state) {
    return state.officersInOrgDashboard.totalItems;
  },

  getOfficerById(state) {
    return (id) =>
      state.officersInOrgDashboard.officerList.find((x) => x.id === id);
  },

  ssoSync(state) {
    const lastSyncDate = parseUtcDate(state.ssoSync.lastSyncDate);
    const orgTimeZoneId = state.organization.timeZoneId;

    return {
      ...state.ssoSync,
      lastSyncDate: lastSyncDate
        ? formatSsoLastSyncDate(lastSyncDate, orgTimeZoneId)
        : '',
    };
  },

  // For now we will consider any non-Performa account to be SSO
  isSSO(state) {
    return (
      state.organization.authenticationType !== AUTHENTICATION_TYPES.PERFORMA
    );
  },
  // Currently Azure AD is the only SSO that also has a user sync
  hasSSOSync(state) {
    return (
      state.organization.authenticationType === AUTHENTICATION_TYPES.AZURE ||
      state.organization.authenticationType === AUTHENTICATION_TYPES.OKTA
    );
  },

  // UPN is needed for Orgs that have single sign on, but no sync capability
  needsUPN(_state, getters) {
    return getters.isSSO && !getters.hasSSOSync;
  },

  getCourseProgressById(state) {
    return (id) => {
      const match = state.courses.find((x) => x.id == id);
      return match;
    };
  },

  getAssignableCourseId(state) {
    return (id) => {
      const match = state.organization.courses.find((x) => x.id == id);
      return match.assignableCourseOverrideId;
    };
  },

  getExternalAttributesByUIArea(state) {
    return (uiArea) =>
      state.organization.externalAttributes.filter((ea) =>
        ea.uiAreas.includes(uiArea)
      );
  },

  accreditingEntityUserIdDisplayName(state) {
    return state.organization?.accreditingEntityUserIdDisplayName;
  },

  accreditingEntityName(state) {
    return state.organization?.accreditingEntityName;
  },
};
